/**
 * Лоадер с текстом текущей стадии cold-path сборки snapshot.
 *
 * Используется вместо плейн `<Loader />` в местах, где ожидается
 * длинный запрос к серверу (RepoPage: ожидание `/projects` и
 * `/snapshot-v2`). Параллельно с основным запросом polling'ует
 * `/progress` каждые 500ms и отображает текстовый этап + таймер
 * прошедших секунд.
 *
 * Warm path (snapshot уже кэширован) отдаёт ответ за <100ms — этот
 * компонент успеет показать только дефолтный «Подготавливаем загрузку…»
 * и тут же размонтируется. Это допустимо.
 */

import { Center, Loader, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { fetchProgress } from '../lib/api-client';

interface StageLoaderProps {
    owner:   string;
    repo:    string;
    gitRef?: string;
}

export function StageLoader({ owner, repo, gitRef }: StageLoaderProps): JSX.Element {
    const progressQ = useQuery({
        queryKey:        ['progress', owner, repo, gitRef],
        queryFn:         () => fetchProgress({ owner, repo, ref: gitRef }),
        refetchInterval: 500,
        staleTime:       0,
        retry:           false,
    });

    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setElapsed(e => e + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const stage = progressQ.data?.stage;
    const label =
        stage === 'cloning'  ? 'Клонируем репозиторий…' :
        stage === 'parsing'  ? 'Парсим .pflow файлы…' :
        stage === 'building' ? 'Собираем snapshot…' :
                               'Подготавливаем загрузку…';

    return (
        <Center mih="60vh">
            <Stack align="center" gap="xs">
                <Loader />
                <Text>{label}</Text>
                <Text size="xs" c="dimmed">{elapsed}s</Text>
            </Stack>
        </Center>
    );
}
